/**
 * Multi-shop checkout — one cart with items from several shops, one checkout, one order per shop.
 *
 * Everything here is driven by what the SERVER returned: a cart is multi-shop only when the server priced it that
 * way (``cart.multi_shop`` + ``cart.shop_groups``), which it does only for tenants with the feature switched on by
 * Super Admin. For every other cart each helper returns its "nothing changes" value (false / [] / {} / the input
 * itself), so the screens that call them render and post exactly what they did before. The server enforces every
 * rule again; hiding an option here is convenience, not security.
 */
export const MULTI_SHOP_KEY = "multi_shop_checkout";

/** The tenant has the feature (``tenant.capabilities`` is resolved by the server; absent when off). */
export const multiShopOn = (tenant) => !!tenant?.capabilities?.[MULTI_SHOP_KEY]?.enabled;

export const isMultiShopCart = (cart) => !!cart?.multi_shop && Array.isArray(cart.shop_groups) && cart.shop_groups.length > 1;

export const shopGroups = (cart) => (isMultiShopCart(cart) ? cart.shop_groups : []);

/** A shop in the cart that can't be ordered from right now (closed, doesn't deliver to the address). */
export const shopIssues = (cart) => (isMultiShopCart(cart) ? cart.issues || [] : []);

/** What a header shows for "where is this cart from". */
export const cartShopLabel = (cart) => (isMultiShopCart(cart) ? `${cart.shop_groups.length} shops` : cart?.restaurant_name);

/** Cash on delivery for this cart: untouched for a single-shop cart; for several shops only when the tenant allows it. */
export const codAllowedFor = (cart) => !isMultiShopCart(cart) || !!cart?.multi_shop_rules?.cod_allowed;

/** The payment methods to offer — the SAME object for a single-shop cart. */
export function payMethodsFor(cart, methods) {
    if (!methods || codAllowedFor(cart) || !methods.cod) return methods;
    return { ...methods, cod: false };
}

const cartKey = (cart) => JSON.stringify([cart?.id, (cart?.items || []).map((i) => [i.product_id, i.variant_id || "", i.quantity])]);

const newId = () => {
    try {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    } catch { /* older WebViews */ }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
};

/**
 * Fields added to the place-order payload — ``{}`` for a single-shop cart, so its payload is unchanged.
 * ``client_request_id`` stays the same for the same cart contents (a second tap, a retry after a lost response) and
 * changes as soon as the cart does, so the server can answer a repeat with the checkout it already made.
 * ``ref`` is a ``useRef`` owned by the calling screen.
 */
export function multiShopPayload(ref, cart) {
    if (!isMultiShopCart(cart)) return {};
    const key = cartKey(cart);
    if (!ref.current || ref.current.key !== key) ref.current = { key, id: newId() };
    return { client_request_id: ref.current.id };
}

/** Where to go after ``POST /orders/checkout`` — the checkout page for several orders, null for an ordinary order. */
export const orderGroupPath = (data) => (data?.order_group_id ? `/order-groups/${data.order_group_id}` : null);

export const ORDER_STATUS_LABEL = {
    PLACED: "Placed", CONFIRMED: "Confirmed", PREPARING: "Preparing", READY: "Ready", PICKED_UP: "Picked up",
    OUT_FOR_DELIVERY: "Out for delivery", DELIVERED: "Delivered", CANCELLED: "Cancelled", REJECTED: "Rejected",
};

export const OVERALL_LABEL = {
    IN_PROGRESS: "In progress", DELIVERED: "All delivered", CANCELLED: "Cancelled",
    PARTIALLY_DELIVERED: "Completed", PAYMENT_PENDING: "Waiting for payment", PROCESSING: "Placing your orders",
    FAILED: "Not placed", EXPIRED: "Not placed", REFUND_DUE: "Refund in progress", REFUNDED: "Refunded",
    REFUND_FAILED: "Refund in progress",
};
